import { LayersClear as LayersClearIcon } from "@mui/icons-material";
import { IconButton } from "@mui/material";
import { isNil, isNumber } from "lodash";
import { FC, useEffect, useMemo, useRef, useState, useTransition } from "react";
import { useTranslation } from "react-i18next";

import { SVG_MUI } from "@ryde/assets";
import {
  useDropdownSourceContext,
  useDynamicFormsUtils,
  useDynamicGridTranslate,
  usePageContext,
  useRidesNavigation,
} from "@ryde/redux";
import {
  FIELD_TYPE_IDS,
  fieldNameMetadataResolve,
  GROUPING_GRID_NO_GROUPS,
  isEmptyValue,
  isGroupingValueDelayEnum,
  isISODateStr,
  isLiveMapDelayStatusGroupingColumn,
  NO_VALUE_DISPLAY,
  normalizeGridValue,
  resolveLiveMapDelayStatusForDisplay,
  RideDelayStatusEnum,
  translateGridValue,
  useFormats,
} from "@ryde/shared";

import { FramerTooltip } from "../../common";
import { LiveMapGroupingHead } from "../cell-templates";
import { usePagingStickyEmulation } from "../paging";
import { GroupingAreaStyled } from "./grid.styled";

interface IGroupingArea {
  groupRowsCount?: number;
  ungroupFn: () => void;
  toggleCollapseFn?: () => void;
  collapsed?: boolean;
  // TODO refactor this we don't want null or undefined as group value
  groupingValue:
    string | boolean | null | undefined | typeof GROUPING_GRID_NO_GROUPS;
  columnName: string;
}

export const GroupingArea: FC<IGroupingArea> = ({
  ungroupFn,
  collapsed,
  toggleCollapseFn,
  groupRowsCount,
  columnName,
  groupingValue,
}) => {
  const { entity } = usePageContext();
  const { formatDateTimeNormalized } = useFormats();
  const { t } = useTranslation();
  const stickyElRef = useRef<HTMLDivElement>(null);
  usePagingStickyEmulation({ pagingRef: stickyElRef });
  const { getGridControlByGridFieldName } = useDynamicFormsUtils();
  const { getGridLabelComputed } = useDynamicGridTranslate();
  const { getDropdownLabel } = useDropdownSourceContext();
  const [dropdownDisplayValue, setDropdownDisplayValue] =
    useState<string>(NO_VALUE_DISPLAY);
  const [, startTransition] = useTransition();

  const titleComputed = useMemo(() => {
    if (dropdownDisplayValue !== NO_VALUE_DISPLAY) {
      return dropdownDisplayValue;
    }

    let columnHeaderTitlePrefix = "";
    const gridControl = getGridControlByGridFieldName(columnName);

    if (gridControl) {
      const gridColumnHeaderTextTranslated = getGridLabelComputed(
        entity,
        gridControl,
      );

      if (gridColumnHeaderTextTranslated) {
        columnHeaderTitlePrefix = `${gridColumnHeaderTextTranslated}: `;
      }
    }

    if (
      groupingValue === false ||
      groupingValue === "false" ||
      isNil(groupingValue)
    ) {
      return `${columnHeaderTitlePrefix}${t("selectListLabel.No")}`;
    }

    if (groupingValue === true || groupingValue === "true") {
      return `${columnHeaderTitlePrefix}${t("selectListLabel.Yes")}`;
    }

    if (
      isNil(groupingValue) ||
      isEmptyValue(groupingValue) ||
      !gridControl ||
      groupingValue === GROUPING_GRID_NO_GROUPS
    ) {
      return `${columnHeaderTitlePrefix}${NO_VALUE_DISPLAY}`;
    }

    const {
      gridAttributes,
      formAttributes,
      name: fieldNameRaw,
      isColumn,
      jsonType,
    } = gridControl;

    if (!groupingValue) {
      return jsonType === "boolean"
        ? t("selectListLabel.No")
        : NO_VALUE_DISPLAY;
    }

    const fieldNameResolved = fieldNameMetadataResolve(fieldNameRaw, isColumn);

    const gridValueNormalized = normalizeGridValue({
      valueToProcess: groupingValue,
      fieldNameResolved,
      entity,
    });
    const gridValueTranslated = translateGridValue({
      valueToProcess: gridValueNormalized,
      formAttributes,
      gridAttributes,
      skipBooleanSelectListLabelConversion: false,
      t,
    });

    if (isISODateStr(groupingValue)) {
      return formatDateTimeNormalized(groupingValue);
    }

    return gridValueTranslated;
  }, [
    dropdownDisplayValue,
    getGridControlByGridFieldName,
    columnName,
    groupingValue,
    entity,
    t,
    getGridLabelComputed,
    formatDateTimeNormalized,
  ]);

  const { isRidesMapView } = useRidesNavigation();

  const delayStatusGroupingControl = useMemo(
    () => getGridControlByGridFieldName(columnName),
    [columnName, getGridControlByGridFieldName],
  );

  const isDelayStatusGroupingColumn = useMemo(
    () =>
      isLiveMapDelayStatusGroupingColumn(
        columnName,
        delayStatusGroupingControl?.gridFieldTypeId,
      ),
    [columnName, delayStatusGroupingControl?.gridFieldTypeId],
  );

  const isRideLiveMapGrouping = useMemo((): boolean => {
    if (!isRidesMapView || !isDelayStatusGroupingColumn) {
      return false;
    }

    if (
      groupingValue === undefined ||
      groupingValue === null ||
      groupingValue === ""
    ) {
      return true;
    }

    return isGroupingValueDelayEnum(
      columnName,
      groupingValue,
      delayStatusGroupingControl?.gridFieldTypeId,
    );
  }, [
    columnName,
    delayStatusGroupingControl?.gridFieldTypeId,
    groupingValue,
    isDelayStatusGroupingColumn,
    isRidesMapView,
  ]);

  useEffect(() => {
    const gridControl = getGridControlByGridFieldName(columnName);

    const getItems = async (): Promise<void> => {
      if (!gridControl) {
        return;
      }

      if (!groupingValue || !isNumber(groupingValue)) {
        return;
      }

      if (gridControl?.formAttributes?.dropItemsSource) {
        const label = await getDropdownLabel(
          gridControl?.formAttributes.dropItemsSource,
          groupingValue,
        );
        setDropdownDisplayValue(label);
      }
    };

    if (
      gridControl?.formFieldTypeId === FIELD_TYPE_IDS.FORM.dropdown_bound &&
      !gridControl?.isColumn &&
      !gridControl?.gridAttributes?.fieldNameGrid
    ) {
      startTransition(() => {
        getItems();
      });
    }
  }, [
    getDropdownLabel,
    getGridControlByGridFieldName,
    columnName,
    groupingValue,
  ]);

  const delayStatusForGrouping = isRideLiveMapGrouping
    ? resolveLiveMapDelayStatusForDisplay(
        groupingValue as RideDelayStatusEnum | null | undefined,
      )
    : undefined;

  return (
    <GroupingAreaStyled ref={stickyElRef} $delayStatus={delayStatusForGrouping}>
      {!collapsed && (
        <SVG_MUI.ChevronDownSVG
          className="collapse_toggle"
          title="Collapse grouping"
          data-e2e="svg-chevron-down-grouping"
          onClick={() => toggleCollapseFn?.()}
        />
      )}
      {!!collapsed && (
        <SVG_MUI.ChevronUpSVG
          className="collapse_toggle"
          title="Collapse grouping"
          data-e2e="svg-chevron-up-grouping"
          onClick={() => toggleCollapseFn?.()}
        />
      )}
      {!isRideLiveMapGrouping && (
        <span className="group-area__text">{String(titleComputed)}</span>
      )}
      {!!isRideLiveMapGrouping && delayStatusForGrouping && (
        <LiveMapGroupingHead delayStatus={delayStatusForGrouping} />
      )}
      {!!groupRowsCount && (
        <label className="rows_count">{groupRowsCount}</label>
      )}
      <FramerTooltip content={t("general.UNGROUP")}>
        <IconButton onClick={ungroupFn}>
          <LayersClearIcon
            style={{ fontSize: "12px", marginInlineStart: "4px" }}
          />
        </IconButton>
      </FramerTooltip>
    </GroupingAreaStyled>
  );
};
